import React, {useMemo, useEffect} from 'react';
import moment from 'moment';
import {Link} from 'react-router-dom';
import {useTable, usePagination, useFilters, useRowSelect} from 'react-table';
import {Table, Popup} from 'semantic-ui-react';
import {
  Filter,
  DefaultColumnFilter,
  SelectColumnFilter,
  IndeterminateCheckbox,
  IndeterminateAllCheckbox,
} from '../common/TableFilters';
import {shortDate} from '../../../common/constants/datesFormat';
import TablePaginator from '../common/TablePaginator';

const columnsTableWithProps = (props) => {
  return [
    {
      'Header': 'DEALER ID',
      'accessor': 'dealerId',
      'width': 150,
    },
    {
      'Header': 'DEALER',
      'accessor': 'dealer',
      'width': 250,
    },
    {
      'Header': 'NUM TWC DEALER',
      'accessor': 'numTwcDealer',
      'width': 150,
    },
    {
      'Header': 'FECHA REPARACIÓN O/R',
      'accessor': 'repairDate',
      'width': 150,
      'Cell': ({row}) => {
        const repairDate = row.original && row.original.repairDate;
        return moment(repairDate).format(shortDate);
      },
    },
    {
      'Header': 'FECHA DEL TWC',
      'accessor': 'dateTwc',
      'width': 150,
      'Cell': ({row}) => {
        const dateTwc = row.original && row.original.dateTwc;
        return moment(dateTwc).format(shortDate);
      },
    },
    {
      'Header': 'VEHÍCULO',
      'accessor': 'chassis',
      'width': 250,
    },
    {
      'Header': 'TIPO GARANTÍA',
      'accessor': 'warrantyType',
      'width': 150,
      'Cell': ({row}) => {
        return row.type || 'TWC';
      },
    },
    {
      'Header': 'TIPO TWC',
      'accessor': 'type',
      'width': 150,
      'Filter': SelectColumnFilter,
    },
    {
      'Header': 'ESTADO TWC',
      'accessor': 'twcStatusDescription',
      'width': 150,
    },
    {
      'Header': 'USUARIO CREACIÓN',
      'accessor': 'createdUser',
      'width': 150,
    },
    {
      'Header': 'NÚMERO GARANTÍA',
      'accessor': 'code',
      'width': 150,
    },
    {
      'Header': 'TOTAL GARANTÍA',
      'accessor': 'totalTwc',
      'width': 150,
    },
    {
      'Header': 'NÚMERO TWC DEFINITIVO',
      'accessor': 'twcNumDefinitive',
      'width': 150,
    },
    {
      'Header': 'CTA. COBRO DEFINITIVA',
      'accessor': 'twcAccountDefinitive',
      'width': 150,
    },
    {
      'id': 'actions',
      'Header': 'ACCIONES',
      'Cell': ({row}) => {
        const {code, numTwcDealer, dealerId} = row.original;
        return (
          <Popup
            trigger={
              <Link
                to={`/editar-garantias-admin/${code}/${numTwcDealer}/${dealerId}`}
                target='_blank'
              >
                <span className='link'>
                  <i className='material-icons' disabled={props.loading}>
                    visibility
                  </i>
                </span>
              </Link>
            }
            content='Ver Garantía'
            position='top center'
            inverted
          />
        );
      },
    },
    {
      'id': 'processCheckBox',
      'Header': 'PROCESAR',
      'defaultCanFilter': true,
      'filter': props.selectedStatus,
      'Filter': IndeterminateAllCheckbox,
      'Cell': ({row}) => {
        const {code, checked} = row.original;
        return (
          <IndeterminateCheckbox
            checked={checked}
            code={code}
          />
        );
      },
    },
  ];
};

const getRowValue = (row) => {
  if (row && row.original) {
    const daysAmount = (row.original && row.original.daysAmount) || 0;
    if (daysAmount >= 15) {
      return {
        'style': {
          'color': '#9f3a38',
          'background': '#fff6f6',
        },
      };
    }
    if (daysAmount >= 10) {
      return {
        'style': {
          'color': '#573a08',
          'background': '#fffaf3',
        },
      };
    }
  }
  return {};
};

const QueryTable = (props) => {
  const {selectedStatus, processedWarrantyList, handleChange, filteredRows} = props;
  const columnsTable = columnsTableWithProps(props);
  const data = useMemo(() => processedWarrantyList, [processedWarrantyList]);
  const columns = useMemo(() => columnsTable, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    rows,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    'state': {pageIndex, pageSize},
  } = useTable(
    {
      columns,
      data,
      'defaultColumn': {'Filter': DefaultColumnFilter},
      'initialState': {'pageIndex': 0, 'pageSize': 10},
      'autoResetFilters': false,
    },
    useFilters,
    usePagination,
    useRowSelect,
  );

  useEffect(() => {
    if (rows.length !== filteredRows.length) {
      handleChange('filteredRows', rows);
    }
  }, [rows]);

  return (
    <>
      <div className='table-container'>
        <Table className='interactive' compact striped {...getTableProps()}>
          <Table.Header>
            {headerGroups.map((headerGroup) => (
              <Table.Row key='headers' {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, index) => (
                  <Table.HeaderCell
                    key={`title-${index}`}
                    textAlign='center'
                    {...column.getHeaderProps()}
                  >
                    {column.render('Header')}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            ))}
            {headerGroups.map((headerGroup) => (
              <Table.Row key='filters' id='filters'>
                {headerGroup.headers.map((column, index) => (
                  <Table.HeaderCell
                    key={`filter-${index}`}
                    className={index === 7 ? 'dropdown-header' : ''}
                    textAlign='center'
                  >
                    <Filter column={column} selectedStatus={selectedStatus} />
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            ))}
          </Table.Header>
          <Table.Body {...getTableBodyProps()}>
            {!page.length && (
              <Table.Row>
                <Table.Cell colSpan={15} textAlign='center'>
                  No hay datos disponibles
                </Table.Cell>
              </Table.Row>
            )}
            {page.length > 0 &&
              page.map((row, i) => {
                prepareRow(row);
                return (
                  <Table.Row key={i} {...row.getRowProps(getRowValue(row))}>
                    {row.cells.map((cell) => {
                      return (
                        <Table.Cell textAlign='center' {...cell.getCellProps()}>
                          {cell.render('Cell')}
                        </Table.Cell>
                      );
                    })}
                  </Table.Row>
                );
              })}
          </Table.Body>
        </Table>
      </div>
      <TablePaginator
        data={data}
        canPreviousPage={canPreviousPage}
        pageIndex={pageIndex}
        pageOptions={pageOptions}
        pageSize={pageSize}
        canNextPage={canNextPage}
        nextPage={nextPage}
        gotoPage={gotoPage}
        previousPage={previousPage}
        setPageSize={setPageSize}
      />
    </>
  );
};

export default QueryTable;
